<template>
    <div v-loading.fullscreen.lock="fullscreenLoading" class="advertisement-create">
        <div class="settings-field">
            <div class="basic-settings">
                <Name v-model:name="name" label="Название объявления"
                    placeholder="Название объявления" :show-campaign-id-input="false" />
                <Period v-model:period="period" label="Период объявления" 
                    :min-date="minPeriodDate" :max-date="maxPeriodDate" />
                <Type v-model:type="type" />

                <Comment v-model:comment="comment" placeholder="Комментарий к объявлению (необязательно)" />

                <button @click="submitAdvertisements" class="submit" :disabled="!canSubmit">
                    Сохранить объявления
                </button>
            </div>

            <div class="system-settings">
                <GoogleMap 
                    :frames="mapFrames" 
                    v-model:selectedFrameGids="selectedScreenGids"
                    :disablePriceCheck="true"
                    :can-manage-test-frames="!!(user && user.is_admin)"
                />
                <AdvertisementOptions 
                    :screens="screens"
                    :selectedScreenGids="selectedScreenGids"
                    :creativesByOid="creativesByOid"
                    :creativesWithoutOid="creativesWithoutOid"
                    :type="type"
                    :advertisementPeriod="period"
                    v-model:screenSettings="screenSettings"
                />
            </div>
        </div>

        <!-- Debug: API payload preview -->
        <div v-if="false" class="debug-panel">
            <h4>API Payload Preview (для каждого экрана):</h4>
            <pre>{{ JSON.stringify(buildApiPayloads(), null, 2) }}</pre>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

import Name from '../../common/Name.vue';
import Period from '../../common/Period.vue';
import Comment from '../../common/Comment.vue';
import Type from '../../common/Type.vue';
import GoogleMap from '../../common/GoogleMap.vue';
import AdvertisementOptions from './AdvertisementOptions.vue';
import { getRtbDateString } from '../../../utils/timezones';

export default {
    name: 'AdvertisementCreateExternal',
    components: {
        Name,
        Period,
        Comment,
        Type,
        GoogleMap,
        AdvertisementOptions,
    },
    props: {
        oid: {
            type: String,
            required: true,
        },
        campaignExternal: {
            type: Object,
            required: true,
        },
        screens: {
            type: Array,
            required: true,
        },
        creativesByOid: {
            type: Object,
            default: () => ({}),
        },
        creativesWithoutOid: {
            type: Array,
            default: () => [],
        },
        user: {
            type: Object,
            default: null,
        },
    },
    data() {
        const today = getRtbDateString();
        return {
            fullscreenLoading: false,
            name: 'ad_' + (this.campaignExternal.name || ''),
            period: { 
                start: today, 
                end: '' 
            },
            type: 'picture',
            comment: '',
            selectedScreenGids: [],
            screenSettings: {}, // { [gid]: { creativeIds, timeSettings, period } }
        };
    },
    computed: {
        // Конвертируем screens в формат frames для GoogleMap
        mapFrames() {
            return this.screens.map(screen => ({
                gid: screen.gid,
                address: screen.frame?.address || screen.gid,
                city: screen.frame?.city || '',
                lat: screen.frame?.latitude || screen.frame?.lat,
                lon: screen.frame?.longitude || screen.frame?.lon,
                azimuth: screen.frame?.azimuth ?? null,
                width: screen.frame?.width,
                height: screen.frame?.height,
                prices: screen.frame?.prices || [],
                isActive: true, // Для объявлений не проверяем цены
                // Дополнительные данные для отображения
                oid: screen.oid,
                name: screen.name,
            }));
        },
        // Минимальная дата периода - максимум из (сегодня, минимальный period_start всех screens)
        minPeriodDate() {
            const today = getRtbDateString();
            const minScreenDate = this.screens.reduce((min, s) => {
                const start = s.period_start ? s.period_start.split(' ')[0] : null;
                if (!start) return min;
                return (!min || start < min) ? start : min;
            }, null);
            
            if (!minScreenDate) return today;
            return minScreenDate > today ? minScreenDate : today;
        },
        // Максимальная дата периода - минимальный period_end из всех screens
        maxPeriodDate() {
            return this.screens.reduce((min, s) => {
                const end = s.period_end ? s.period_end.split(' ')[0] : null;
                if (!end) return min;
                return (!min || end < min) ? end : min;
            }, null) || '';
        },
        canSubmit() {
            if (this.selectedScreenGids.length === 0) return false;
            if (!this.period.start || !this.period.end) return false;
            
            // Проверяем, что для каждого выбранного экрана выбран хотя бы один креатив
            return this.selectedScreenGids.every(gid => {
                const settings = this.screenSettings[gid];
                return settings && settings.creativeIds && settings.creativeIds.length > 0;
            });
        },
    },
    watch: {
        // При изменении общего периода, обновляем периоды для каждого экрана
        period: {
            deep: true,
            handler(newPeriod) {
                this.selectedScreenGids.forEach(gid => {
                    if (this.screenSettings[gid]) {
                        this.screenSettings[gid].period = { ...newPeriod };
                    }
                });
            },
        },
        // При выборе/снятии экранов, инициализируем настройки
        selectedScreenGids: {
            handler(newGids, oldGids) {
                // Добавляем настройки для новых экранов
                newGids.forEach(gid => {
                    if (!this.screenSettings[gid]) {
                        const screen = this.screens.find(s => s.gid === gid);
                        // Копируем timeSettings из подкампании как дефолт
                        const defaultTimeSettings = this.copyTimeSettings(screen?.timeSettings || []);
                        this.screenSettings[gid] = {
                            creativeIds: [],
                            timeSettings: defaultTimeSettings,
                            period: { ...this.period },
                            // Данные для построения API payload
                            campaign_id: screen?.campaign_id,
                            oid: screen?.oid,
                            api_id: screen?.api_id,
                        };
                    }
                });
                // Удаляем настройки для снятых экранов
                Object.keys(this.screenSettings).forEach(gid => {
                    if (!newGids.includes(gid)) {
                        delete this.screenSettings[gid];
                    }
                });
            },
        },
    },
    methods: {
        /**
         * Формирует payload для API для каждого выбранного экрана
         */
        buildApiPayloads() {
            const updatedMs = Date.now();
            
            return this.selectedScreenGids.map(gid => {
                const screen = this.screens.find(s => s.gid === gid);
                const settings = this.screenSettings[gid] || {};
                
                // Форматируем даты в ISO8601
                const periodStart = this.formatDateTimeISO(settings.period?.start || this.period.start, true);
                const periodEnd = this.formatDateTimeISO(settings.period?.end || this.period.end, false);
                
                // Получаем api_id креативов
                const creativeApiIds = this.getCreativeApiIds(gid, settings.creativeIds || []);
                
                return {
                    // id будет сгенерирован сервером
                    campaignId: screen?.api_id, // api_id подкампании
                    name: this.name || screen?.name || '',
                    period: {
                        start: periodStart,
                        end: periodEnd,
                    },
                    updated: updatedMs,
                    status: 'approve',
                    comment: this.comment || '',
                    type: this.type,
                    timeSettings: this.formatTimeSettings(settings.timeSettings || []),
                    creatives: creativeApiIds,
                    inventories: [{ id: gid }],
                    // Дополнительные данные для сохранения в БД
                    _internal: {
                        campaign_id: screen?.campaign_id,
                        oid: screen?.oid,
                        gid: gid,
                    },
                };
            });
        },
        
        /**
         * Форматирует дату в ISO8601
         */
        formatDateTimeISO(dateStr, isStart) {
            if (!dateStr) return '';
            const date = dateStr.split(' ')[0];
            const time = isStart ? '00:00:00' : '23:59:59';
            return `${date}T${time}`;
        },
        
        /**
         * Форматирует timeSettings для API
         */
        formatTimeSettings(timeSettings) {
            if (!Array.isArray(timeSettings) || timeSettings.length === 0) {
                return null;
            }
            return timeSettings.map(ts => ({
                day: ts.day,
                startTime: ts.startTime,
                endTime: ts.endTime,
            }));
        },
        
        /**
         * Копирует timeSettings из подкампании для инициализации объявления.
         * timeSettings может быть объектом { [day]: [{startTime, endTime}] } или массивом [{day, startTime, endTime}]
         */
        copyTimeSettings(timeSettings) {
            if (!timeSettings) {
                return [];
            }
            
            // Если это массив - просто копируем
            if (Array.isArray(timeSettings)) {
                return timeSettings.map(ts => ({
                    day: ts.day,
                    startTime: ts.startTime,
                    endTime: ts.endTime,
                }));
            }
            
            // Если это объект по дням - конвертируем в массив
            const result = [];
            Object.keys(timeSettings).forEach(day => {
                const intervals = timeSettings[day];
                if (Array.isArray(intervals)) {
                    intervals.forEach(interval => {
                        result.push({
                            day: parseInt(day, 10),
                            startTime: interval.startTime,
                            endTime: interval.endTime,
                        });
                    });
                }
            });
            return result;
        },
        
        /**
         * Получает api_id креативов по их id
         */
        getCreativeApiIds(gid, creativeIds) {
            const screen = this.screens.find(s => s.gid === gid);
            if (!screen) return [];
            
            const oid = screen.oid;
            const allCreatives = [
                ...(this.creativesByOid[oid] || []),
                ...this.creativesWithoutOid,
            ];
            
            return creativeIds
                .map(id => {
                    const creative = allCreatives.find(c => c.id === id);
                    return creative?.api_id || null;
                })
                .filter(apiId => apiId !== null);
        },
        
        async submitAdvertisements() {
            if (!this.canSubmit) {
                this.$notify({
                    title: 'Ошибка',
                    message: 'Выберите экраны и креативы для каждого экрана',
                    type: 'error',
                });
                return;
            }

            this.fullscreenLoading = true;

            try {
                // Формируем данные для сохранения в БД
                const advertisements = this.selectedScreenGids.map(gid => {
                    const screen = this.screens.find(s => s.gid === gid);
                    const settings = this.screenSettings[gid] || {};
                    
                    // Развертываем Proxy в обычные объекты для сериализации
                    const timeSettings = (settings.timeSettings || []).map(ts => ({
                        day: ts.day,
                        startTime: ts.startTime,
                        endTime: ts.endTime,
                    }));
                    
                    return {
                        campaign_id: screen?.campaign_id,
                        gid: gid,
                        oid: screen?.oid,
                        name: this.name || screen?.name,
                        type: this.type,
                        period: settings.period ? { ...settings.period } : { ...this.period },
                        creatives: [...(settings.creativeIds || [])],
                        comment: this.comment,
                        timeSettings: timeSettings,
                    };
                });

                const response = await axios.post(
                    `/guaranteed/campaigns/external/${this.campaignExternal.id}/advertisements/create`,
                    { advertisements }
                );

                this.fullscreenLoading = false;
                this.$notify({
                    title: 'Успех',
                    message: `Создано ${advertisements.length} объявлений`,
                    type: 'success',
                });

                // Перенаправляем на страницу кампаний
                setTimeout(() => {
                    window.location.href = '/guaranteed/campaigns';
                }, 1500);

            } catch (e) {
                this.fullscreenLoading = false;
                const errorMessage = e.response?.data?.error || 'Не удалось создать объявления';
                this.$notify({
                    title: 'Ошибка',
                    message: errorMessage,
                    type: 'error',
                });
            }
        },
    },
};
</script>

<style scoped>
.debug-panel {
    margin-top: 20px;
    padding: 16px;
    background: #f5f5f5;
    border-radius: 8px;
    font-size: 12px;
}
.debug-panel pre {
    white-space: pre-wrap;
    word-break: break-all;
}
</style>
